import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

export function AppLauncherSheet({
  open = false,
  onClose,
  className = "",
  children = null,
  mobileBreakpoint = 768,
  anchorRef = null,
  initialFocusRef = null,
}) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return true;
    try {
      return window.matchMedia(`(max-width: ${mobileBreakpoint}px)`).matches;
    } catch {
      return true;
    }
  });

  useEffect(() => {
    if (!open) return undefined;
    if (typeof window === "undefined") return undefined;
    let mq = null;
    try {
      mq = window.matchMedia(`(max-width: ${mobileBreakpoint}px)`);
    } catch {
      mq = null;
    }
    if (!mq) return undefined;
    const onChange = () => setIsMobile(!!mq.matches);
    onChange();
    try {
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    } catch {
      mq.addListener?.(onChange);
      return () => mq.removeListener?.(onChange);
    }
  }, [mobileBreakpoint, open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape" && typeof onClose === "function") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose, open]);

  useEffect(() => {
    if (!open) return undefined;
    if (typeof document === "undefined") return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    if (typeof document === "undefined") return undefined;
    const focusEl = initialFocusRef?.current;
    if (focusEl && typeof focusEl.focus === "function") {
      focusEl.focus();
      return;
    }
    const fallback = document.getElementById("platform-app-launcher-sheet-content");
    if (fallback && typeof fallback.focus === "function") fallback.focus();
  }, [initialFocusRef, open]);

  const rootClassName = [
    "platformAppLauncherSheet",
    isMobile ? "platformAppLauncherSheet--mobile" : "platformAppLauncherSheet--desktop",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const desktopStyle = useMemo(() => {
    if (!open) return undefined;
    if (isMobile || typeof window === "undefined") return undefined;
    const rect = anchorRef?.current?.getBoundingClientRect?.();
    const margin = 8;
    const maxWidth = 448;
    const vw = window.innerWidth || 0;
    const vh = window.innerHeight || 0;
    const top = rect ? Math.max(margin, Math.min(vh - margin, rect.bottom + 8)) : 72;
    const leftRaw = rect ? rect.left : margin;
    const left = Math.max(margin, Math.min(vw - maxWidth - margin, leftRaw));
    return { top: `${top}px`, left: `${left}px` };
  }, [anchorRef, isMobile, open]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div className={rootClassName}>
      <div
        className="platformAppLauncherSheet__backdrop"
        onClick={() => {
          if (typeof onClose === "function") onClose();
        }}
      />
      <div
        id="platform-app-launcher-sheet-content"
        className="platformAppLauncherSheet__content"
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        style={desktopStyle}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body
  );
}

